import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { Timestamp, doc, getDoc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Grid,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconPlus, IconTrash, IconX } from "@tabler/icons-react";

import {
  Customer,
  FamilyRelative,
  RmProfile,
  RmProfileImportantDate,
  RmProfileKeyPerson,
  RmProfileRelativeAnnotation,
} from "../../types";
import { useFirestore } from "../../helpers/firebaseContext";
import { parseDayFirst } from "../../helpers/common";
import { MyUserDataContext } from "../../contexts";
import CustomProductComment from "../../components/custom_product_comment";

interface CustomerRelationshipProps {
  customerData: Customer;
}

// Everything in the form maps 1:1 onto the rm_profile/main doc; the audit
// fields are stamped at save time instead.
type RmProfileFormValues = Omit<RmProfile, "created_at" | "updated_at" | "updated_by">;

const KEY_PERSON_RELATIONS = [
  "spouse",
  "son",
  "daughter",
  "father",
  "mother",
  "brother",
  "sister",
  "assistant",
  "friend",
  "other",
];

const PREFERRED_CHANNELS = [
  { value: "call", label: "Phone call" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "sms", label: "SMS" },
  { value: "email", label: "Email" },
  { value: "in_person", label: "In person" },
];

const PREFERRED_LANGUAGES = [
  { value: "ar", label: "Arabic" },
  { value: "en", label: "English" },
];

// relatives exists in both legacy map and current array shapes (see add_family_member_modal.tsx)
const relativesToArray = (value: Customer["relatives"]): FamilyRelative[] =>
  Array.isArray(value) ? value : value && typeof value === "object" ? Object.values(value) : [];

const emptyAnnotation = (): RmProfileRelativeAnnotation => ({ interests: "", notes: "" });

const newKeyPerson = (): RmProfileKeyPerson => ({
  id: crypto.randomUUID(),
  name: "",
  relation: "other",
  age: "",
  occupation_or_school: "",
  interests: "",
  notes: "",
});

const newImportantDate = (): RmProfileImportantDate => ({
  id: crypto.randomUUID(),
  label: "",
  date: "",
});

const buildFormValues = (
  data: RmProfile | undefined,
  linkedRelatives: FamilyRelative[],
): RmProfileFormValues => {
  const relativeAnnotations: Record<string, RmProfileRelativeAnnotation> = {};
  linkedRelatives.forEach((relative) => {
    if (relative.relative_id) {
      relativeAnnotations[relative.relative_id] = {
        ...emptyAnnotation(),
        ...data?.relative_annotations?.[relative.relative_id],
      };
    }
  });

  return {
    alias_en: data?.alias_en || "",
    alias_ar: data?.alias_ar || "",
    preferred_language: data?.preferred_language || "",
    preferred_channel: data?.preferred_channel || "",
    best_contact_time: data?.best_contact_time || "",
    contact_notes: data?.contact_notes || "",
    relative_annotations: relativeAnnotations,
    key_people: (data?.key_people || []).map((person) => ({
      ...newKeyPerson(),
      ...person,
      id: person.id || crypto.randomUUID(),
    })),
    interests: data?.interests || [],
    gift_notes: data?.gift_notes || "",
    dietary_notes: data?.dietary_notes || "",
    topics_to_avoid: data?.topics_to_avoid || "",
    employer: data?.employer || "",
    job_title: data?.job_title || "",
    business_interests: data?.business_interests || "",
    referral_source: data?.referral_source || "",
    life_events: data?.life_events || "",
    important_dates: (data?.important_dates || []).map((entry) => ({
      ...newImportantDate(),
      ...entry,
      id: entry.id || crypto.randomUUID(),
    })),
  };
};

export default function CustomerRelationship({ customerData }: CustomerRelationshipProps) {
  const db = useFirestore();
  const navigate = useNavigate();
  const userContext = useContext(MyUserDataContext);

  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<{ at?: Timestamp; by?: string }>({});
  const createdAtRef = useRef<RmProfile["created_at"] | undefined>(undefined);

  const linkedRelatives = useMemo(
    () => relativesToArray(customerData.relatives),
    [customerData.relatives],
  );

  // Memoized so CustomProductComment doesn't resubscribe every render
  const profileRef = useMemo(
    () => doc(db, "customers", customerData.id as string, "rm_profile", "main"),
    [db, customerData.id],
  );

  const form = useForm<RmProfileFormValues>({
    initialValues: buildFormValues(undefined, linkedRelatives),
  });

  useEffect(() => {
    let cancelled = false;
    getDoc(profileRef)
      .then((snapshot) => {
        if (cancelled) return;
        const data = snapshot.data() as RmProfile | undefined;
        createdAtRef.current = data?.created_at;
        setLastUpdated({ at: data?.updated_at as Timestamp | undefined, by: data?.updated_by });
        const values = buildFormValues(data, linkedRelatives);
        form.setValues(values);
        form.resetDirty(values);
        setLoaded(true);
      })
      .catch((error) => {
        console.error("Failed to load RM profile", error);
        if (!cancelled) {
          notifications.show({
            color: "red",
            message: error?.message || "Failed to load relationship profile",
          });
          setLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileRef]);

  const handleSave = async () => {
    const user = userContext.user;
    const values = form.values;
    setSaving(true);
    try {
      const now = Timestamp.now();
      await setDoc(
        profileRef,
        {
          ...values,
          created_at: createdAtRef.current ?? now,
          updated_at: now,
          updated_by: user?.email || user?.uid || "",
        },
        { merge: true },
      );
      createdAtRef.current = createdAtRef.current ?? now;
      setLastUpdated({ at: now, by: user?.email || "" });
      form.resetDirty(values);
      notifications.show({
        color: "green",
        icon: <IconCheck size={16} />,
        message: "Relationship profile saved",
      });
    } catch (error: any) {
      console.error("Failed to save RM profile", error);
      notifications.show({
        color: "red",
        icon: <IconX size={16} />,
        message: error?.message || "Failed to save relationship profile",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return <Loader />;
  }

  const lastUpdatedDate =
    lastUpdated.at && typeof (lastUpdated.at as Timestamp).toDate === "function"
      ? (lastUpdated.at as Timestamp).toDate()
      : undefined;

  return (
    <Stack gap="md" mt="md">
      <Group justify="space-between" align="center">
        <div>
          <Title order={3}>Relationship Profile</Title>
          {lastUpdatedDate && (
            <Text size="xs" c="dimmed">
              Last updated {lastUpdatedDate.toLocaleString()} {lastUpdated.by ? `by ${lastUpdated.by}` : ""}
            </Text>
          )}
        </div>
        <Button onClick={handleSave} loading={saving} disabled={!form.isDirty()}>
          Save changes
        </Button>
      </Group>

      <Paper shadow="xs" p="md" withBorder>
        <Title order={4} mb="sm">Identity & Contact Preferences</Title>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput label="Alias / preferred name (EN)" placeholder="e.g. Abu Khaled" {...form.getInputProps("alias_en")} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput label="Alias / preferred name (AR)" placeholder="مثال: أبو خالد" {...form.getInputProps("alias_ar")} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Select
              label="Preferred language"
              placeholder="Select"
              data={PREFERRED_LANGUAGES}
              clearable
              {...form.getInputProps("preferred_language")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Select
              label="Preferred channel"
              placeholder="Select"
              data={PREFERRED_CHANNELS}
              clearable
              {...form.getInputProps("preferred_channel")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Best time to contact"
              placeholder="e.g. After 5pm, never Friday mornings"
              {...form.getInputProps("best_contact_time")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Contact notes"
              placeholder="e.g. Calls go through his assistant first"
              {...form.getInputProps("contact_notes")}
            />
          </Grid.Col>
        </Grid>
      </Paper>

      <Paper shadow="xs" p="md" withBorder>
        <Group justify="space-between" align="center" mb="sm">
          <Title order={4}>Family & Key People</Title>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => form.insertListItem("key_people", newKeyPerson())}
          >
            Add person
          </Button>
        </Group>

        {linkedRelatives.length > 0 && (
          <Stack gap="xs" mb="md">
            <Text size="sm" fw={600} c="dimmed">Linked clients (from Family Members)</Text>
            {linkedRelatives.map((relative) => (
              <Paper key={relative.relative_id} p="sm" withBorder>
                <Group justify="space-between" align="center" mb="xs">
                  <Group gap="xs">
                    <Anchor
                      size="sm"
                      fw={600}
                      onClick={() => navigate(`/customers/${relative.relative_id}`)}
                    >
                      {relative.relative_name_en || relative.relative_name_ar || relative.relative_id}
                    </Anchor>
                    <Badge size="sm" variant="light">{relative.relation}</Badge>
                    <Badge size="sm" color="teal" variant="light">Linked client</Badge>
                  </Group>
                  {relative.relative_civilID && (
                    <Text size="xs" c="dimmed">Civil ID: {relative.relative_civilID}</Text>
                  )}
                </Group>
                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="Interests"
                      placeholder="e.g. Football, horses"
                      {...form.getInputProps(`relative_annotations.${relative.relative_id}.interests`)}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <TextInput
                      label="Notes"
                      placeholder="RM notes about this person"
                      {...form.getInputProps(`relative_annotations.${relative.relative_id}.notes`)}
                    />
                  </Grid.Col>
                </Grid>
              </Paper>
            ))}
          </Stack>
        )}

        {form.values.key_people?.length === 0 && linkedRelatives.length === 0 && (
          <Text size="sm" c="dimmed">No family members or key people recorded yet.</Text>
        )}

        <Stack gap="xs">
          {(form.values.key_people || []).map((person, index) => (
            <Paper key={person.id} p="sm" withBorder>
              <Grid align="flex-end">
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <TextInput label="Name" placeholder="Name" {...form.getInputProps(`key_people.${index}.name`)} />
                </Grid.Col>
                <Grid.Col span={{ base: 6, sm: 3, md: 2 }}>
                  <Select
                    label="Relation"
                    data={KEY_PERSON_RELATIONS}
                    allowDeselect={false}
                    {...form.getInputProps(`key_people.${index}.relation`)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 6, sm: 3, md: 1 }}>
                  <TextInput label="Age" placeholder="e.g. 12" {...form.getInputProps(`key_people.${index}.age`)} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                  <TextInput
                    label="Occupation / school"
                    placeholder="e.g. KU engineering student"
                    {...form.getInputProps(`key_people.${index}.occupation_or_school`)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 5, md: 2 }}>
                  <TextInput
                    label="Interests"
                    placeholder="e.g. Gaming, karate"
                    {...form.getInputProps(`key_people.${index}.interests`)}
                  />
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 1, md: 1 }}>
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    title="Remove person"
                    onClick={() => form.removeListItem("key_people", index)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Grid.Col>
                <Grid.Col span={12}>
                  <TextInput
                    label="Notes"
                    placeholder="Anything else worth remembering"
                    {...form.getInputProps(`key_people.${index}.notes`)}
                  />
                </Grid.Col>
              </Grid>
            </Paper>
          ))}
        </Stack>
      </Paper>

      <Paper shadow="xs" p="md" withBorder>
        <Title order={4} mb="sm">Interests & Personal Preferences</Title>
        <Grid>
          <Grid.Col span={12}>
            <TagsInput
              label="Interests"
              placeholder="Type an interest and press Enter"
              {...form.getInputProps("interests")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Textarea
              label="Gift notes"
              placeholder="What's been given, what to avoid"
              autosize
              minRows={2}
              {...form.getInputProps("gift_notes")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Textarea
              label="Topics to avoid"
              placeholder="Sensitivities the RM should know about"
              autosize
              minRows={2}
              {...form.getInputProps("topics_to_avoid")}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <TextInput
              label="Dietary notes"
              placeholder="Allergies / restrictions for events"
              {...form.getInputProps("dietary_notes")}
            />
          </Grid.Col>
        </Grid>
      </Paper>

      <Paper shadow="xs" p="md" withBorder>
        <Title order={4} mb="sm">Professional & Wealth Context</Title>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <TextInput label="Employer" {...form.getInputProps("employer")} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <TextInput label="Job title" {...form.getInputProps("job_title")} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <TextInput
              label="Referral source"
              placeholder="Who introduced them"
              {...form.getInputProps("referral_source")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Textarea
              label="Business interests"
              placeholder="Ownerships, board seats, family business"
              autosize
              minRows={2}
              {...form.getInputProps("business_interests")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <Textarea
              label="Upcoming life events"
              placeholder="e.g. Selling company, retirement, inheritance"
              autosize
              minRows={2}
              {...form.getInputProps("life_events")}
            />
          </Grid.Col>
        </Grid>
      </Paper>

      <Paper shadow="xs" p="md" withBorder>
        <Group justify="space-between" align="center" mb="sm">
          <Title order={4}>Important Dates</Title>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => form.insertListItem("important_dates", newImportantDate())}
          >
            Add date
          </Button>
        </Group>
        {form.values.important_dates?.length === 0 && (
          <Text size="sm" c="dimmed">No dates recorded yet — birthdays, anniversaries, graduations.</Text>
        )}
        <Stack gap="xs">
          {(form.values.important_dates || []).map((entry, index) => (
            <Group key={entry.id} align="flex-end" wrap="nowrap">
              <TextInput
                style={{ flex: 1 }}
                label="Occasion"
                placeholder="e.g. Daughter's graduation"
                {...form.getInputProps(`important_dates.${index}.label`)}
              />
              <DateInput
                label="Date"
                placeholder="Select date"
                valueFormat="DD/MM/YYYY"
                dateParser={parseDayFirst}
                firstDayOfWeek={0}
                clearable
                {...form.getInputProps(`important_dates.${index}.date`)}
              />
              <ActionIcon
                color="red"
                variant="subtle"
                mb={4}
                title="Remove date"
                onClick={() => form.removeListItem("important_dates", index)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          ))}
        </Stack>
      </Paper>

      <Paper shadow="xs" p="md" withBorder>
        <CustomProductComment collectionRef={profileRef} />
      </Paper>
    </Stack>
  );
}
